"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ORDER_PPN_RECOMMENDATIONS_CACHE_KEY, ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY } from "@/src/lib/data-fetchers/order-query-keys";
import { getEligibilityCheckByOrderId, getEligibilityCheckByReferralId, getEligibilityCheckByTaskId, recheckOrderEligibility, recheckTaskEligibility, verifyInsuranceEligibilityAtIntake, type VerifyInsuranceEligibilityRequestPayload } from "@/src/requests/stedi-eligibility-check";
export const STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "stedi-eligibility-check";
export const INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "intake-stedi-eligibility-check";
export function useStediEligibilityCheck(referralId) {
  const $ = _c(12);
  let t0;
  let t1;
  if ($[0] !== referralId) {
    t0 = [STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId];
    t1 = () => {
      if (!referralId) {
        return null;
      }
      return getEligibilityCheckByReferralId(referralId);
    };
    $[0] = referralId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!referralId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const query = useQuery(t3);
  const t4 = query.data ?? null;
  let t5;
  if ($[7] !== query.isError || $[8] !== query.isFetching || $[9] !== query.isLoading || $[10] !== t4) {
    t5 = {
      eligibilityCheck: t4,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError
    };
    $[7] = query.isError;
    $[8] = query.isFetching;
    $[9] = query.isLoading;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function useRecheckReferralEligibility(taskId, referralId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== taskId) {
    t0 = () => recheckTaskEligibility(taskId);
    $[0] = taskId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient || $[3] !== referralId) {
    t1 = data => {
      if (data.check) {
        queryClient.setQueryData([STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId], data.check);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId]
      });
    };
    $[2] = queryClient;
    $[3] = referralId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
export function useIntakeStediEligibilityCheck(taskId) {
  const $ = _c(12);
  let t0;
  let t1;
  if ($[0] !== taskId) {
    t0 = [INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId];
    t1 = () => {
      if (!taskId) {
        return null;
      }
      return getEligibilityCheckByTaskId(taskId);
    };
    $[0] = taskId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!taskId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const query = useQuery(t3);
  const t4 = query.data ?? null;
  let t5;
  if ($[7] !== query.isError || $[8] !== query.isFetching || $[9] !== query.isLoading || $[10] !== t4) {
    t5 = {
      eligibilityCheck: t4,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError
    };
    $[7] = query.isError;
    $[8] = query.isFetching;
    $[9] = query.isLoading;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function useVerifyInsuranceEligibilityAtIntake(taskId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== taskId) {
    t0 = payload => {
      if (!taskId) {
        throw new Error("Task id is required to verify insurance eligibility");
      }
      return verifyInsuranceEligibilityAtIntake(taskId, payload);
    };
    $[0] = taskId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient || $[3] !== taskId) {
    t1 = data => {
      if (!taskId) {
        return;
      }
      queryClient.setQueryData([INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId], data.check);
    };
    $[2] = queryClient;
    $[3] = taskId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
export function useOrderStediEligibilityCheck(orderId) {
  const $ = _c(12);
  let t0;
  let t1;
  if ($[0] !== orderId) {
    t0 = [ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId];
    t1 = () => {
      if (!orderId) {
        return null;
      }
      return getEligibilityCheckByOrderId(orderId);
    };
    $[0] = orderId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!orderId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const query = useQuery(t3);
  const t4 = query.data ?? null;
  let t5;
  if ($[7] !== query.isError || $[8] !== query.isFetching || $[9] !== query.isLoading || $[10] !== t4) {
    t5 = {
      eligibilityCheck: t4,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError
    };
    $[7] = query.isError;
    $[8] = query.isFetching;
    $[9] = query.isLoading;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function useRecheckOrderEligibility(orderId, _patientId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== orderId) {
    t0 = () => {
      if (!orderId) {
        throw new Error("Order id is required to re-check insurance eligibility");
      }
      return recheckOrderEligibility(orderId, true);
    };
    $[0] = orderId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== orderId || $[3] !== queryClient) {
    t1 = data => {
      if (!orderId) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [ORDER_PPN_RECOMMENDATIONS_CACHE_KEY, orderId]
      });
      if (data.check) {
        queryClient.setQueryData([ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId], data.check);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId]
      });
    };
    $[2] = orderId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}